import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";

// Import slice - PATH RELATIVI!
import {
    Ostacolo,
    OstacoliList,
    TIPI_OSTACOLO,
    getOstacolo,
    saveOstacolo,
    deleteOstacolo,
    setOstacoloCorrenteId,
} from "./ostacoliSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";

// Form
import {useForm, SubmitHandler, Controller} from "react-hook-form";
import useAutoSave from "../../common/formUtility/useAutoSave";
import AutoSaveIndicator from "../../common/formUtility/AutoSaveIndicator";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import CircularProgress from "@mui/material/CircularProgress";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {convertToDateTime} from "../../common/dateUtils/convertToDateTime";

// ============================================
// INTERFACES
// ============================================

interface OstacoloFormProps {
    ostacoloId?: number;
    onClose: () => void;
    onSaved?: () => void;
    odpId?: number; // per pre-impostare l'OdP quando si crea da dettaglio OdP
}

// ============================================
// COMPONENT
// ============================================

const OstacoloForm = ({ostacoloId, onClose, onSaved, odpId}: OstacoloFormProps) => {
    const dispatch = useAppDispatch();

    // Legge l'ostacolo dallo slice
    const ostacoliList = useSelector<RootState, OstacoliList>(
        (state) => state.ostacoliState.ostacoliList
    );
    const ostacolo = ostacoliList.results.find((o) => o.id === ostacoloId);

    // Al mount, se è un ostacolo esistente, caricalo nello slice
    useEffect(() => {
        if (ostacoloId) {
            dispatch(setOstacoloCorrenteId(ostacoloId));
            dispatch(getOstacolo({ostacoloId}));
        }
    }, [ostacoloId]);

    const defaultValues: Ostacolo = ostacolo || {
        odp: odpId,
        tipo: "MAT",
        descrizione: "",
        data_prevista_risoluzione: undefined,
        data_risoluzione: null,
        risolto: false,
        note_risoluzione: "",
    };

    const {
        handleSubmit,
        register,
        formState: {errors, isDirty, isValid},
        control,
        watch,
        reset,
        setValue,
        setError,
        clearErrors,
    } = useForm<Ostacolo>({
        defaultValues,
        mode: "onChange",
    });

    const listaCampi = Object.keys(defaultValues) as Array<keyof Ostacolo>;
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

    // Reset form quando cambia l'ostacolo
    useEffect(() => {
        reset(ostacolo || defaultValues);
    }, [ostacolo?.id]);

    // Gestione errori server
    // Per ostacoli esistenti: errori dal record in lista
    // Per ostacoli nuovi: errori da ostacoliList.errorsStack
    const currentErrorsStack = ostacoloId
        ? ostacolo?.errorsStack
        : ostacoliList.errorsStack;

    useEffect(() => {
        clearErrors();
        listaCampi.forEach((field) => {
            if (currentErrorsStack?.fieldsErrors?.[field]) {
                setError(field, {
                    type: "server",
                    message: currentErrorsStack.fieldsErrors[field].toString(),
                });
            }
        });
    }, [currentErrorsStack?.fieldsErrors]);

    const onSubmit: SubmitHandler<Ostacolo> = async (data) => {
        setSaving(true);
        setServerError(null);
        const risposta = await dispatch(saveOstacolo({ostacoloToSave: data}));
        setSaving(false);
        if (risposta.meta.requestStatus === "fulfilled") {
            reset(data);
            onSaved?.();
            // In creazione chiudi il dialog
            if (!ostacoloId) {
                onClose();
            }
        } else {
            // Mostra errore generico se non ci sono errori sui campi
            const error = risposta.payload || risposta.meta.requestStatus;
            setServerError(
                typeof error === "string"
                    ? error
                    : "Errore durante il salvataggio"
            );
        }
    };

    // AutoSave solo per ostacoli esistenti
    useAutoSave({
        watch,
        handleSubmit,
        isDirty,
        isValid,
        delay: 600,
        onSubmit,
        canSubmit: !!ostacoloId,
        errors,
    });

    const handleDelete = async () => {
        if (ostacoloId && ostacolo) {
            await dispatch(deleteOstacolo({ostacoloToDelete: ostacolo}));
            onSaved?.();
            onClose();
        }
    };

    const ostacoloStatus = currentErrorsStack?.status || ErrorStatusTypes.OK;

    return (
        <Box sx={{pt: 1}}>
            {currentErrorsStack?.fieldsErrors?.non_field_errors && (
                <Alert severity="error" sx={{mb: 2}}>
                    {currentErrorsStack.fieldsErrors.non_field_errors.toString()}
                </Alert>
            )}
            {serverError && (
                <Alert severity="error" sx={{mb: 2}}>
                    {serverError}
                </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    {/* Info read-only per ostacoli esistenti */}
                    {ostacoloId && ostacolo && (
                        <Grid size={{xs: 12}}>
                            <Stack direction="row" spacing={2} sx={{alignItems: "center", mb: 1}}>
                                <Typography variant="body2" color="text.secondary">
                                    {ostacolo.odp_display}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {ostacolo.codice_articolo} - {ostacolo.nome_cliente}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Segnalato: {convertToDateTime(ostacolo.data_segnalazione)?.toFormat("dd/MM/yyyy")}
                                </Typography>
                                {ostacolo.is_scaduto && (
                                    <Chip
                                        icon={<WarningAmberIcon />}
                                        label={`Scaduto di ${ostacolo.giorni_ritardo} giorni`}
                                        color="error"
                                        size="small"
                                    />
                                )}
                                <AutoSaveIndicator
                                    isDirty={isDirty}
                                    status={ostacoloStatus}
                                    variant="chip"
                                    size="small"
                                />
                            </Stack>
                        </Grid>
                    )}

                    {/* OdP (solo per nuovo, se non pre-impostato) */}
                    {!ostacoloId && !odpId && (
                        <Grid size={{xs: 12, sm: 4}}>
                            <TextField
                                label="ID OdP"
                                type="number"
                                size="small"
                                fullWidth
                                {...register("odp", {required: "OdP obbligatorio"})}
                                error={!!errors?.odp}
                                helperText={errors?.odp?.message}
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </Grid>
                    )}

                    {/* Tipo */}
                    <Grid size={{xs: 12, sm: 4}}>
                        <Controller
                            name="tipo"
                            control={control}
                            rules={{required: "Tipo obbligatorio"}}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Tipo ostacolo"
                                    size="small"
                                    fullWidth
                                    error={!!errors?.tipo}
                                    helperText={errors?.tipo?.message}
                                    slotProps={{inputLabel: {shrink: true}}}
                                >
                                    {TIPI_OSTACOLO.map((tipo) => (
                                        <MenuItem key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                    </Grid>

                    {/* Data prevista risoluzione */}
                    <Grid size={{xs: 12, sm: 4}}>
                        <TextField
                            label="Data vincolo"
                            type="date"
                            size="small"
                            fullWidth
                            {...register("data_prevista_risoluzione")}
                            error={!!errors?.data_prevista_risoluzione}
                            helperText={errors?.data_prevista_risoluzione?.message}
                            slotProps={{
                                inputLabel: {shrink: true},
                                input: {
                                    endAdornment: watch("data_prevista_risoluzione") ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setValue("data_prevista_risoluzione", "", {shouldDirty: true})}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined,
                                },
                            }}
                        />
                    </Grid>

                    {/* Descrizione */}
                    <Grid size={{xs: 12}}>
                        <TextField
                            label="Descrizione"
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            {...register("descrizione")}
                            error={!!errors?.descrizione}
                            helperText={errors?.descrizione?.message}
                            slotProps={{inputLabel: {shrink: true}}}
                        />
                    </Grid>

                    {/* Risoluzione */}
                    <Grid size={{xs: 12}}>
                        <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                            <Controller
                                name="risolto"
                                control={control}
                                render={({field}) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value || false}
                                                onChange={field.onChange}
                                                size="small"
                                            />
                                        }
                                        label="Risolto"
                                    />
                                )}
                            />
                        </Stack>
                    </Grid>

                    {/* Data risoluzione e note (visibili se risolto) */}
                    {watch("risolto") && (
                        <>
                            <Grid size={{xs: 12, sm: 4}}>
                                <TextField
                                    label="Data risoluzione"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    {...register("data_risoluzione")}
                                    error={!!errors?.data_risoluzione}
                                    helperText={errors?.data_risoluzione?.message}
                                    slotProps={{
                                        inputLabel: {shrink: true},
                                        input: {
                                            endAdornment: watch("data_risoluzione") ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setValue("data_risoluzione", null, {shouldDirty: true})}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{xs: 12, sm: 8}}>
                                <TextField
                                    label="Note risoluzione"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    {...register("note_risoluzione")}
                                    error={!!errors?.note_risoluzione}
                                    helperText={errors?.note_risoluzione?.message}
                                    slotProps={{inputLabel: {shrink: true}}}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* Azioni */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{justifyContent: "space-between", mt: 3}}
                >
                    <Box>
                        {ostacoloId && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleDelete}
                            >
                                Elimina
                            </Button>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                        >
                            Chiudi
                        </Button>
                        {!ostacoloId && (
                            <Button
                                variant="contained"
                                size="small"
                                type="submit"
                                disabled={!isValid || saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                            >
                                {saving ? "Creazione..." : "Crea"}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </form>
        </Box>
    );
};

export default OstacoloForm;
